<template>
  <div id="print-modal" class="setting">
    <EprintLoadingOverlay />
    <div
      class="setting-item setting-item-eprint"
      :style="{ opacity: isLoading ? 0.3 : 1 }"
    >
      <div ref="sectionPrintType" class="print-price">
        <div v-if="hasRecommendedOption && recommendedOption" class="default">
          <p class="default_tag_txt">
            <span class="default_tag">おすすめ</span>
            {{ currentTemplateConfig.recommendedText }}
          </p>
          <div class="default_block">
            <EprintPrintOptionRadio
              :option="recommendedOption"
              @click-option="handlePrintTypeChange"
            />
          </div>
        </div>
        <el-collapse
          v-if="hasOtherOptions"
          v-model="session.otherSizesCollapse"
          class="default_block default_block--list other-sizes-collapse"
          @change="handleOtherSizesToggle"
        >
          <el-collapse-item
            title="他のサイズ・決済方法もお選びいただけます"
            name="otherSizes"
          >
            <div
              :class="
                session.printFlag === 'nophotolong' ||
                session.printFlag === 'letter' ||
                session.printFlag === 'taishokutodoke' ||
                session.printFlag === 'keireki'
                  ? 'list-flex'
                  : ''
              "
            >
              <div
                v-for="option in otherOptions"
                :key="option.id"
                class="list_block"
              >
                <EprintPrintOptionRadio
                  :option="option"
                  @click-option="handlePrintTypeChange"
                />
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
      <div ref="sectionTerms" class="term eprint-term">
        <p class="eprint-term__link">
          <router-link
            :to="{ name: 'eprintTerms' }"
            target="_blank"
            rel="noopener"
            class="external-link"
            @click.native="handleTermsLinkClick"
            >コンビニ印刷の利用規約</router-link
          >
        </p>
      </div>
      <div ref="sectionPayment" class="eprint-payment">
        <div class="eprint-payment__form">
          <small-payment-method-selector
            ref="paymentSelector"
            :serviceName="'コンビニプリント'"
            :price="currentPaymentPrice"
            :is_subscription="false"
            :is_simultaneous="false"
            :payment_method_used="'credit_card'"
            :print_type="session.printType"
            :default_payment_method="defaultPaymentMethod"
            :format_id="session.formatid"
            @request-seven-eleven-payment="actions.handleSevenElevenClick"
            @payment-success="actions.handlePaymentSuccess"
            @paypay-payment="actions.handlePaypayPayment"
            @credit-card-payment-start="actions.handleCreditCardPaymentStart"
            @payment-error="actions.handlePaymentError"
          />
        </div>
      </div>
      <div class="print_memo">
        <p class="print_memo01">
          ※コンビニ印刷の<a
            href="https://www.e-printservice.net/shohin/yagish"
            id="footer-link-eprint"
            target="_blank"
            rel="nofollow"
            class="external-link"
            >詳しい利用方法を確認する</a
          >
        </p>
        <p class="print_memo01">
          ※OSが古い場合など、レイアウトが崩れる場合があります。また、3ページ目入ると、コンビニ印刷には対応しておりません。プレビューで内容をご確認のうえ、ご購入ください。
        </p>
        <p class="print_memo02">
          ※ご不明点等ございましたら、<a
            rel="nofollow"
            href="/contact"
            target="_blank"
            class="print_memo02__link"
            >こちらのフォーム</a
          >よりご連絡ください。
        </p>
      </div>
    </div>
    <EprintPaypayNoticeDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import SmallPaymentMethodSelector from '@/components/Payment/SmallPaymentMethodSelector.vue'
import EprintLoadingOverlay from '@/components/format-edit/eprint/EprintLoadingOverlay.vue'
import EprintPaypayNoticeDialog from '@/components/format-edit/eprint/EprintPaypayNoticeDialog.vue'
import EprintPrintOptionRadio from '@/components/format-edit/eprint/EprintPrintOptionRadio.vue'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  useEprintActions,
  useEprintDerived,
  useEprintSession
} from '@/composables/format-edit/useEprintSession'
import { isCashlessSupported } from '@/utils/format-edit/eprintOptions'

type PaymentSelectorVm = {
  isConvinienceButtonDisabled: boolean
  isCardButtonDisabled: boolean
  isPaypayButtonDisabled: boolean
}

const session = useEprintSession()
const actions = useEprintActions()
const { site } = useHostServices()
const funnel = useEprintFunnel()
const {
  currentPaymentPrice,
  currentTemplateConfig,
  recommendedOption,
  otherOptions,
  hasRecommendedOption,
  hasOtherOptions,
  isLoading,
  defaultEprintPaymentMethod
} = useEprintDerived(session)

const defaultPaymentMethod = computed(() =>
  defaultEprintPaymentMethod(site.appFlag)
)

const paymentSelector = ref<PaymentSelectorVm | null>(null)
const sectionPrintType = ref<HTMLElement | null>(null)
const sectionTerms = ref<HTMLElement | null>(null)
const sectionPayment = ref<HTMLElement | null>(null)

onMounted(() => {
  session.paymentSelectorApi = {
    resetDisabled() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = false
      selector.isCardButtonDisabled = false
      selector.isPaypayButtonDisabled = false
    },
    lockAll() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = true
      selector.isCardButtonDisabled = true
      selector.isPaypayButtonDisabled = true
    },
    unlockConvenience() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = false
    }
  }
})
onBeforeUnmount(() => {
  session.paymentSelectorApi = null
})

function trackSection(name: string) {
  if (!session.funnelSessionId || !session.dialogLayoutReady) return
  if (session.viewedSections[name]) return
  session.viewedSections[name] = true
  funnel.send('section_view', {
    section: name,
    format_id: session.formatid,
    print_type: session.printType,
    is_cashless_available: isCashlessSupported({ id: session.printType }),
    funnel_session_id: session.funnelSessionId
  })
}

function maybeTrack(el: HTMLElement | null, name: string) {
  if (!el || !session.dialogLayoutReady) return
  const rect = el.getBoundingClientRect()
  if (rect.top < window.innerHeight && rect.bottom > 0) trackSection(name)
}

useIntersectionObserver(sectionPrintType, ([entry]) => {
  if (entry?.isIntersecting) trackSection('print_type')
})
useIntersectionObserver(sectionTerms, ([entry]) => {
  if (entry?.isIntersecting) trackSection('terms')
})
useIntersectionObserver(sectionPayment, ([entry]) => {
  if (entry?.isIntersecting) trackSection('payment_method')
})

watch(
  () => session.dialogLayoutReady,
  (ready) => {
    if (!ready) return
    maybeTrack(sectionPrintType.value, 'print_type')
    maybeTrack(sectionTerms.value, 'terms')
    maybeTrack(sectionPayment.value, 'payment_method')
  }
)

function handlePrintTypeChange(clickedId: string) {
  if (
    !session.dialogOpenedAt ||
    Date.now() - session.dialogOpenedAt.getTime() < 500
  ) {
    return
  }
  if (session.funnelSessionId) {
    const isRecheck =
      session.previousClickedPrintType !== null &&
      String(session.previousClickedPrintType) === String(clickedId)
    funnel.send('scroll_to_payment', {
      print_type: String(clickedId),
      is_cashless: isCashlessSupported({ id: clickedId }),
      is_recheck: isRecheck,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
    session.previousClickedPrintType = clickedId
  }
  nextTick(() => {
    scrollChildIntoScrollableAncestor(sectionPayment.value)
  })
}

/** 見えている支払い欄へはスクロールしない（overlay を上端へ巻き上げない）。 */
function scrollChildIntoScrollableAncestor(target: HTMLElement | null) {
  if (!target) return
  let container: HTMLElement | null = target.parentElement
  while (container && container !== document.documentElement) {
    const overflowY = window.getComputedStyle(container).overflowY
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      container.scrollHeight > container.clientHeight + 1
    if (canScroll) {
      const cRect = container.getBoundingClientRect()
      const tRect = target.getBoundingClientRect()
      if (tRect.top >= cRect.top && tRect.bottom <= cRect.bottom) return
      const delta =
        tRect.top < cRect.top
          ? tRect.top - cRect.top
          : tRect.bottom - cRect.bottom
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: 'smooth'
      })
      return
    }
    container = container.parentElement
  }
}

function handleOtherSizesToggle(activeNames: string[]) {
  if (!session.funnelSessionId) return
  funnel.send('other_sizes_toggle', {
    action: activeNames && activeNames.length > 0 ? 'open' : 'close',
    print_type: String(session.printType),
    format_id: session.formatid,
    funnel_session_id: session.funnelSessionId
  })
}

function handleTermsLinkClick() {
  if (!session.funnelSessionId) return
  funnel.send('terms_link_click', {
    format_id: session.formatid,
    funnel_session_id: session.funnelSessionId
  })
}
</script>
